const USER_PLAN_API_URL =
  process.env.REACT_APP_USER_PLAN_API_URL ||
  "https://napi.authkey.io/api/v1/UserPlan";

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type");

  let result;

  if (contentType?.includes("application/json")) {
    result = await response.json();
  } else {
    const text = await response.text();
    result = {
      message: text || "Unexpected API response.",
    };
  }

  if (!response.ok) {
    throw new Error(result?.message || "User Plan API request failed.");
  }

  if (result?.success === false || result?.status === false) {
    throw new Error(result?.message || "User Plan API request failed.");
  }

  return result;
};


export const createUserPlan = async ({
  userId,
  token,
  planTitle,
  planFor,
  planDuration,
  planDescription,
  amountInr,
  amountUsd,
  amountEur,
  planType,
  platformFeeWaiverEnabled,
  platformFeeWaiverAmount,
  channel,
  status,
  humanAgentSeats,
  aiChatAgent,
  includedAiTokens,
  whatsAppChatWidget,
  authkeyBranding,

}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!planTitle) throw new Error("Plan title is missing.");
  if (!planFor) throw new Error("plan_for is missing.");

  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "CreateUserPlan",
      user_id: userId,
      token,
      plan_title: planTitle,
      plan_for: planFor,
      plan_duration: planDuration,
      plan_description: planDescription,
      amount_inr: amountInr,
      amount_usd: amountUsd,
      amount_eur: amountEur,
      plan_type: planType,
      platform_fee_waiver_enabled: platformFeeWaiverEnabled,
      platform_fee_waiver_amount: platformFeeWaiverAmount,
       //sending multiple channelvalues as comma separated string, e.g. "whatsapp,instagram"
      channel: channel ,
      status: status === "active" ? 1 : 0,
      human_agent_seats: humanAgentSeats,
      ai_chat_agent: aiChatAgent,
      included_ai_tokens: includedAiTokens,
      whatsApp_chat_widget: whatsAppChatWidget,
      authkey_branding: authkeyBranding,
    }),
  });

  return parseResponse(response);
};

export const fetchPlans = async ({ userId, token }) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");

  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "fetchPlans",
      user_id: userId,
      token,
    }),
  });

  return parseResponse(response);
};

  //if filter_type = all then no need to send retr_user_id, but if filter_type = user_wise then retr_user_id is required (=6737)
export const fetchUserAssignedPlans = async ({
  userId,
  token,
  filterType = "all",
  retrUserId,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (filterType === "user_wise" && !retrUserId) {
    throw new Error("retr_user_id is required for user_wise filter type.");
  }

  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "fetchUserEnabledPlans",
      user_id: userId,
      token,
      filter_type: filterType,
      //retr_user_id should be integer
      retr_user_id: retrUserId ? parseInt(retrUserId, 10) : undefined,
    }),
  });

  return parseResponse(response);
};

export const updateUserPlan = async ({
  userId,
  token,
  planId,
  planTitle,
  planFor,
  planDuration,
  planDescription,
  amountInr,
  amountUsd,
  amountEur,
  planType,
  introductoryOffer,
  platformFeeWaiverEnabled,
  platformFeeWaiverAmount,
  channel,
  humanAgentSeats,
  aiChatAgent,
  includedAiTokens,
  whatsAppChatWidget,
  authkeyBranding,
  status,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!planId) throw new Error("Plan ID is missing.");
  if (!planTitle) throw new Error("Plan title is missing.");
  if (!planFor) throw new Error("plan_for is missing.");

  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "updatePlan",
      user_id: userId,
      token,
      plan_id: planId,
      plan_title: planTitle,
      plan_for: planFor,
      plan_duration: planDuration,
      plan_description: planDescription,
      amount_inr: amountInr,
      amount_usd: amountUsd,
      amount_eur: amountEur,
      plan_type: planType,
      introductory_offer: introductoryOffer,
      platform_fee_waiver_enabled: platformFeeWaiverEnabled,
      channel: channel,
      human_agent_seats: humanAgentSeats,
      ai_chat_agent: aiChatAgent,
      included_ai_tokens: includedAiTokens,
      whatsApp_chat_widget: whatsAppChatWidget,
      authkey_branding: authkeyBranding,
      platform_fee_waiver_amount: platformFeeWaiverAmount,
      status: status === "active" ? 1 : 0,
    }),
  });

  return parseResponse(response);
};

export const deleteUserPlan = async ({ userId, token, planId }) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!planId) throw new Error("Plan ID is missing.");

  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "deletePlan",
      user_id: userId,
      token,
      plan_id: planId,
    }),
  }); 

  return parseResponse(response);
};

export const updateMainPlan = async ({ userId, token, retrUserId, planId, fields }) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!retrUserId) throw new Error("retr_user_id is missing.");
  if (!planId) throw new Error("Plan ID is missing.");
  if (!fields) throw new Error("Fields are missing.");
 
  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "updateMainPlan",
      user_id: userId,
      token,
      //retr_user_id should be integer
      retr_user_id: retrUserId ? parseInt(retrUserId, 10) : undefined,
      plan_id: planId,
      fields,
    }),
  });

  return parseResponse(response);
}


export const updateAddOnPlan = async ({ userId, token, retrUserId, planId, fields, addOnKey }) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!retrUserId) throw new Error("retr_user_id is missing.");
  if (!planId) throw new Error("Plan ID is missing.");
  if (!addOnKey) throw new Error("addOnKey is missing.");
  if (!fields) throw new Error("Fields are missing.");

  const response = await fetch(USER_PLAN_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "updateAddOnPlan",
      user_id: userId,
      token,
      retr_user_id: retrUserId ? parseInt(retrUserId, 10) : undefined,
      plan_id: planId,
      addOnKey,
      fields,
    }),
  });
  return parseResponse(response);
}

// agar plan title Business hai then pass
// human_agent_seats 5
//           resdata.ai_chat_agent 1 
//           resdata.included_ai_tokens 5m 
//           resdata.whatsapp_chat_widget  1

// if Scale
//  human_agent_seats 7
//           resdata.ai_chat_agent 1 
//           resdata.included_ai_tokens 5m 
//           resdata.whatsapp_chat_widget  1